import cardValidator from 'card-validator'
import { z } from 'zod'
import { getStartOfMonth } from '~/lib/date/date'
import type { DateText } from '~/lib/graphql/scalars'

const zCardBrandSchema = z.enum(['MASTER', 'VISA', 'JCB', 'AMEX', 'DINERS', 'DISCOVER'])
export type CardBrand = z.infer<typeof zCardBrandSchema>

const CARD_BRAND_MAP: Record<string, CardBrand> = {
  // card-validator
  // https://github.com/braintree/credit-card-type?tab=readme-ov-file#api
  mastercard: 'MASTER',
  visa: 'VISA',
  jcb: 'JCB',
  'american-express': 'AMEX',
  'diners-club': 'DINERS',
  // PAY.JP
  // https://pay.jp/docs/payjs
  MasterCard: 'MASTER',
  Visa: 'VISA',
  JCB: 'JCB',
  'American Express': 'AMEX',
  'Diners Club': 'DINERS',
  Discover: 'DISCOVER',
}
const zCardBrandMapSchema = z.string().transform((value) => CARD_BRAND_MAP[value])

export const detectCardBrand = (cardNumber: string): CardBrand | undefined => {
  const brand = cardValidator.number(cardNumber).card?.type
  if (brand === undefined) {
    return undefined
  }
  const parsed = zCardBrandMapSchema.safeParse(brand)
  if (parsed.success) {
    return parsed.data
  }
  return undefined
}
if (import.meta.vitest) {
  const { expect, it } = import.meta.vitest
  it('detectCardBrand', () => {
    expect(detectCardBrand('5555555555554444')).toBe('MASTER')
    expect(detectCardBrand('4111111111111111')).toBe('VISA')
    expect(detectCardBrand('3566002020360505')).toBe('JCB')
    expect(detectCardBrand('378282246310005')).toBe('AMEX')
    expect(detectCardBrand('30569309025904')).toBe('DINERS')
  })
}

export const normalizeCardBrand = (brand: string): CardBrand | undefined => {
  // 既に CardBrand の文字列の場合はそのまま返す
  const parsed = zCardBrandSchema.safeParse(brand)
  if (parsed.success) {
    return parsed.data
  }
  // それ以外の文字列の場合はマッピングを試みる
  const mapped = zCardBrandMapSchema.safeParse(brand)
  if (mapped.success) {
    return mapped.data
  }
  return undefined
}
if (import.meta.vitest) {
  const { expect, it } = import.meta.vitest
  it('normalizeCardBrand', () => {
    expect(normalizeCardBrand('MASTER')).toBe('MASTER')
    expect(normalizeCardBrand('VISA')).toBe('VISA')
    expect(normalizeCardBrand('JCB')).toBe('JCB')
    expect(normalizeCardBrand('AMEX')).toBe('AMEX')
    expect(normalizeCardBrand('DINERS')).toBe('DINERS')
    expect(normalizeCardBrand('DISCOVER')).toBe('DISCOVER')
    expect(normalizeCardBrand('MasterCard')).toBe('MASTER')
    expect(normalizeCardBrand('Visa')).toBe('VISA')
    expect(normalizeCardBrand('JCB')).toBe('JCB')
    expect(normalizeCardBrand('American Express')).toBe('AMEX')
    expect(normalizeCardBrand('Diners Club')).toBe('DINERS')
    expect(normalizeCardBrand('Discover')).toBe('DISCOVER')
  })
}

export function validateCardExpiration(data: {
  year?: string
  month?: string
  visitDate?: DateText
}) {
  if (data.year === undefined || data.month === undefined || data.visitDate === undefined) {
    return undefined
  }
  const expirationDate = `20${data.year}-${Number(data.month) - 1}-01`
  if (getStartOfMonth(data.visitDate) >= expirationDate) {
    return '有効期限（月/年）がご来店日の翌月まで有効なカードを指定し、再度お試し下さい。'
  }
  return undefined
}
if (import.meta.vitest) {
  const { expect, it } = import.meta.vitest
  it('validateCardExpiration', () => {
    const year = '24'
    const month = '01'
    expect(validateCardExpiration({ year, month, visitDate: '2023-12-31' as DateText })).toBe(
      undefined
    )
    expect(validateCardExpiration({ year, month, visitDate: '2024-01-01' as DateText })).toBe(
      '有効期限（月/年）がご来店日の翌月まで有効なカードを指定し、再度お試し下さい。'
    )
    expect(validateCardExpiration({ year, month, visitDate: '2024-01-02' as DateText })).toBe(
      '有効期限（月/年）がご来店日の翌月まで有効なカードを指定し、再度お試し下さい。'
    )
  })
}
